import React, { useState, useEffect } from "react";
import {
  Edit3,
  Save,
  X,
  Phone,
  Mail,
  User,
  Calendar,
  Heart,
  Shield,
  MapPin,
  Clock,
} from "lucide-react";
import axiosInstance from "../../axiosInstace";
import "./Profile.scss";
import LoadingScreen from "../../LoadingScreen/LoadingScreen";
import { toast } from "react-toastify";
const Profile = () => {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState("personal");
  const [form, setForm] = useState({ mobileNumber: "" });

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const res = await axiosInstance.get("/api/access");
        const data = res.data?.data ?? res.data;
        setProfile(data);
        setForm({ mobileNumber: data?.mobileNumber || "" });
      } catch (error) {
        console.error("Failed to fetch profile", error);
      } finally {
        setLoading(false);
      }
    };
    fetchProfile();
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axiosInstance.put(`${process.env.REACT_APP_BACKEND_URL}/api/update/user-details`, {
        mobileNumber: form.mobileNumber || null,
      });
      setProfile((prev) => ({ ...prev, mobileNumber: form.mobileNumber }));
      setEditing(false);
     
      toast.success("Mobile number updated successfully!");
    } catch (error) {
      toast.error("Failed to update mobile number.", error);
      
     
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setForm({ mobileNumber: profile?.mobileNumber || "" });
    setEditing(false);
  };

  const getInitials = (name) => {
    return (
      name
        ?.split(" ")
        .map((n) => n[0])
        .join("")
        .slice(0, 2)
        .toUpperCase() || "U"
    );
  };

  const formatPhoneNumber = (phone) => {
    // 1. Handle null, undefined, or empty cases first.
    if (!phone) {
      return "Not provided";
    }

    // 2. Convert the input to a string to ensure .replace() works.
    const phoneStr = String(phone);

    // 3. Clean and format the string.
    const cleaned = phoneStr.replace(/\D/g, "");
    if (cleaned.length === 10) {
      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(
        6
      )}`;
    }

    // Return the original value (now as a string) if it's not a 10-digit number.
    return phoneStr;
  };

  if (loading) {
    return <LoadingScreen />;
  }

  if (!profile) {
    return <div className="profile-error">Unable to load profile data.</div>;
  }

  return (
    <div className="profile-wrapper">
      {/* Hero Header Section */}
      <div className="profile-hero-section">
        <div className="hero-gradient"></div>
        <div className="hero-content">
          <div className="profile-avatar-container">
            {profile.profileImage ? (
              <img
                src={
                  profile.profileImageType
                    ? `data:${profile.profileImageType};base64,${profile.profileImage}`
                    : profile.profileImage
                }
                alt="Profile"
                className="profile-avatar"
              />
            ) : (
              <div className="profile-avatar initials">
                {getInitials(profile.employeeName)}
              </div>
            )}
            <div className="status-indicator"></div>
          </div>
          <div className="profile-header-info">
            <h1 className="profile-title">
              {profile.employeeName || "Welcome"}
            </h1>
            <p className="profile-subtitle">Self-Discovery Journey</p>
            <div className="profile-badges">
              {profile.authorities?.map((role, index) => (
                <span key={index} className="role-badge">
                  {role.replace("ROLE_", "")}
                </span>
              )) || <span className="role-badge">Explorer</span>}
            </div>
          </div>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="nav-container">
        <div className="nav-tabs">
          <button
            className={`nav-tab ${activeTab === "personal" ? "active" : ""}`}
            onClick={() => setActiveTab("personal")}
          >
            <User size={18} />
            <span>Personal</span>
          </button>
          <button
            className={`nav-tab ${activeTab === "preferences" ? "active" : ""}`}
            onClick={() => setActiveTab("preferences")}
          >
            <Heart size={18} />
            <span>Preferences</span>
          </button>
          <button
            className={`nav-tab ${activeTab === "journey" ? "active" : ""}`}
            onClick={() => setActiveTab("journey")}
          >
            <Calendar size={18} />
            <span>Journey</span>
          </button>
        </div>
      </div>

      {/* Content Section */}
      <div className="content-section">
        {activeTab === "personal" && (
          <div className="content-grid">
            <div className="content-card primary">
              <div className="card-header">
                <div className="card-icon">
                  <Mail size={20} />
                </div>
                <h3 className="card-title">Contact Information</h3>
              </div>
              <div className="card-content">
                <div className="info-row">
                  <label className="info-label">Email Address</label>
                  <span className="info-value">
                    {profile.username || "Not provided"}
                  </span>
                </div>

                <div className="info-row">
                  <label className="info-label">Mobile Number</label>
                  <div className="mobile-field-container">
                    {!editing ? (
                      <div
                        className="mobile-display"
                        onClick={() => setEditing(true)}
                      >
                        <Phone size={16} />
                        <span className="mobile-value">
                          {formatPhoneNumber(profile.mobileNumber)}
                        </span>
                        <button
                          className="edit-trigger"
                          title="Edit mobile number"
                        >
                          <Edit3 size={14} />
                        </button>
                      </div>
                    ) : (
                      <div className="mobile-edit-container">
                        <Phone size={16} />
                        <input
                          type="tel"
                          value={form.mobileNumber || ""}
                          onChange={(e) =>
                            setForm({ mobileNumber: e.target.value })
                          }
                          placeholder="Enter your mobile number"
                          className="mobile-input"
                          autoFocus
                        />
                        <div className="edit-actions">
                          <button
                            onClick={handleSave}
                            disabled={saving}
                            className="save-button"
                            title="Save changes"
                          >
                            <Save size={14} />
                          </button>
                          <button
                            onClick={handleCancel}
                            className="cancel-button"
                            title="Cancel editing"
                          >
                            <X size={14} />
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className="content-card secondary">
              <div className="card-header">
                <div className="card-icon">
                  <Shield size={20} />
                </div>
                <h3 className="card-title">Account Details</h3>
              </div>
              <div className="card-content">
                <div className="info-row">
                  <label className="info-label">Account Status</label>
                  <span className="status-badge active">
                    <div className="status-dot"></div>
                    {profile.status || "Active"}
                  </span>
                </div>
                <div className="info-row">
                  <label className="info-label">Member Since</label>
                  <span className="info-value">January 2024</span>
                </div>
                <div className="info-row">
                  <label className="info-label">Profile Completion</label>
                  <div className="progress-container">
                    <div className="progress-bar">
                      <div
                        className="progress-fill"
                        style={{ width: "75%" }}
                      ></div>
                    </div>
                    <span className="progress-text">75%</span>
                  </div>
                </div>
                <div className="info-row">
                  <label className="info-label">User Roles</label>
                  <div className="roles-container">
                    {profile.authorities?.map((authority, index) => (
                      <span key={index} className="role-chip">
                        {authority.replace("ROLE_", "")}
                      </span>
                    )) || <span className="role-chip">User</span>}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === "preferences" && (
          <div className="content-grid">
            <div className="content-card full-width">
              <div className="card-header">
                <div className="card-icon">
                  <Heart size={20} />
                </div>
                <h3 className="card-title">Your Preferences</h3>
              </div>
              <div className="card-content">
                <div className="coming-soon">
                  <Heart size={48} />
                  <h4>Preference Settings Coming Soon</h4>
                  <p>
                    Customize your self-discovery experience with personalized
                    preferences, goals, and interests. Set your journey pace and
                    areas of focus.
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === "journey" && (
          <div className="content-grid">
            <div className="content-card full-width">
              <div className="card-header">
                <div className="card-icon">
                  <MapPin size={20} />
                </div>
                <h3 className="card-title">Your Journey</h3>
              </div>
              <div className="card-content">
                <div className="coming-soon">
                  <Clock size={48} />
                  <h4>Journey Tracking Coming Soon</h4>
                  <p>
                    Track your progress, milestones, and personal growth over
                    time. View insights about your self-discovery path and
                    achievements.
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Profile;
